import {Injectable, isDevMode} from '@angular/core';
import {Centre} from "../interfaces/Centres";
import {ɵFormGroupValue, ɵTypedOrUntyped} from "@angular/forms";
import {AngularFirestore} from "@angular/fire/compat/firestore";
import {Units} from "../interfaces/Units";
import {FullChild} from "../interfaces/FullChild";
import {MatSnackBar} from "@angular/material/snack-bar";
import firebase from "firebase/compat/app";
import {environment} from "../../environments/environment";
import {AdmissionLogService} from "./admission-log.service";

/** An expected unit conflict whose message is written for the user and can be shown as is. */
export class UnitError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'UnitError';
  }
}

@Injectable({
    providedIn: 'root'
})
export class UnitService {
  env = isDevMode() ? environment.testMode : environment.productionMode
  userData = JSON.parse(localStorage.getItem('userData')!)

  constructor(private mFirestore: AngularFirestore,
              private mAdmissionLogService: AdmissionLogService,
              private _snackBar: MatSnackBar) {

  }


  // Units live in Firestore: {env.units}/{centreCode}/units/{unitKey}
  private unitsPath(centreCode: string) {
    return `${this.env.units}/${centreCode}/units`;
  }

  private unitRef(centreCode: string, unitKey: string) {
    return this.mFirestore.firestore.doc(`${this.unitsPath(centreCode)}/${unitKey}`);
  }

  private centreRef(centreCode: string) {
    return this.mFirestore.firestore.doc(`${this.env.centres}/${centreCode}`);
  }

  private occupyCentre(delta: 1 | -1) {
    return {
      availableUnits: firebase.firestore.FieldValue.increment(-delta),
      occupiedUnits: firebase.firestore.FieldValue.increment(delta),
      updatedAt: new Date()
    };
  }

  /** Turns an error from any unit operation into a message for the snackbar. `action` reads like "discharge the child". */
  errorMessage(err: any, action: string): string {
    if (err instanceof UnitError) return err.message;
    switch (err?.code) {
      case 'permission-denied':
        return `You don't have permission to ${action}. Please contact an administrator.`;
      case 'unavailable':
      case 'deadline-exceeded':
        return `Couldn't ${action} because the server could not be reached. Check your internet connection and try again.`;
      case 'not-found':
        return `Couldn't ${action} because a record it needs no longer exists. Refresh the page and try again.`;
      case 'aborted':
      case 'failed-precondition':
        return `Couldn't ${action} because the data changed while saving. Refresh the page and try again.`;
      default:
        return `Couldn't ${action}. Please try again, and contact support if it keeps happening.`;
    }
  }

  /** Reads the unit inside the transaction and aborts unless it is still free. */
  private async assertAvailable(tx: firebase.firestore.Transaction, ref: firebase.firestore.DocumentReference, unitKey: string) {
    const unit = await tx.get(ref);
    if (!unit.exists) {
      throw new UnitError(`Unit ${unitKey} no longer exists in this centre. Refresh the page and pick another unit.`);
    }
    const data = unit.data()!;
    if (data['status'] === 'Alloted') {
      throw new UnitError(`Unit ${unitKey} has just been allotted to ${data['name'] || data['allotedTo']}. Please pick another unit.`);
    }
    if (data['status'] !== 'Available') {
      throw new UnitError(`Unit ${unitKey} is not available (status: ${data['status']}). Please pick another unit.`);
    }
  }

  /** Reads the unit inside the transaction and aborts unless it is still allotted to `childCode`. */
  private async assertAllottedTo(tx: firebase.firestore.Transaction, ref: firebase.firestore.DocumentReference, unitKey: string, childCode: string) {
    const unit = await tx.get(ref);
    if (!unit.exists) {
      throw new UnitError(`Unit ${unitKey} no longer exists. Refresh the page and try again.`);
    }
    if (unit.data()?.['allotedTo'] !== childCode) {
      throw new UnitError(`Unit ${unitKey} is no longer allotted to ${childCode}; it was changed by someone else. Refresh the page and try again.`);
    }
  }

  //? To Get Units
  getUnit(centre: string) {
    return this.mFirestore.collection<Units>(this.unitsPath(centre), ref => ref.orderBy('order')).valueChanges()
  }

  getNewUnit(centre: string) {
    return this.mFirestore.collection<Units>(
      this.unitsPath(centre),
      ref => ref.where('status', '==', 'Alloted')
    ).valueChanges();
  }

//? For Unit Allotement: checks the unit is still free, then reserves it and moves the centre counters, atomically
  allotUnit(childCode: string, centreCode: string, unitCode: string, childname: string) {
    const unitRef = this.unitRef(centreCode, unitCode)
    return this.mFirestore.firestore.runTransaction(async tx => {
      await this.assertAvailable(tx, unitRef, unitCode)
      tx.update(unitRef, {
        allotedTo: childCode,
        name: childname,
        status: 'Alloted'
      })
      tx.update(this.centreRef(centreCode), this.occupyCentre(1))
    })
  }

//? Undo allotUnit (e.g. when enrolling the child failed after the unit was reserved)
  releaseUnit(centreCode: string, unitCode: string, childCode: string) {
    const unitRef = this.unitRef(centreCode, unitCode)
    return this.mFirestore.firestore.runTransaction(async tx => {
      const unit = await tx.get(unitRef)
      if (!unit.exists || unit.data()?.['allotedTo'] !== childCode) return
      tx.update(unitRef, {allotedTo: 'NA', name: '', status: 'Available'})
      tx.update(this.centreRef(centreCode), this.occupyCentre(-1))
    })
  }

//? To allot floor to unit
  allotFloor(selectedCentre: Centre, unit: string, floorselected: string | undefined) {
    return this.unitRef(selectedCentre.centreCode, unit).update({
      'floorNo': floorselected
    })
  }

  dischargeProcess(dischargeForm: any, child: FullChild, setReminder: boolean) {
    // change unit status
    //change child status
    // change center available and occupied status
    // set followup reminder
    return this.dischargeBatch(dischargeForm, child, setReminder)
  }


  private async dischargeBatch(dischargeForm: any, child: FullChild, setReminder: boolean) {
    if (!child?.childCode || !child?.centreCode) {
      throw new UnitError('This child has no centre on record, so they cannot be discharged. Please check the child\'s details.')
    }
    console.log('CHILD', child)
    // ? Updating Unit Details
    // ? Updating Child Details

    const updateChildDetailsRef = this.mFirestore.firestore.doc(`${this.env.child}/${child.childCode}`)
    const dischargeArrayObject = {
      centreCode: child?.centreCode,
      currentUnit: child?.selectUnit,
      dateOfDischarge: dischargeForm?.dischargeDate,
      weight: dischargeForm?.weight,
      height: dischargeForm?.height,
      type: 'Discharge',
      reason: dischargeForm?.reason,

      //centerName: data?.centreName
    }

    // OM SHANTI = the child has expired: same status/flag the OM SHANTI approval sets, and no follow-up
    const isOmShanti = dischargeForm?.reason === 'OM SHANTI'
    if (isOmShanti) setReminder = false

    const updateChildData: any = {
      status: isOmShanti ? 'OM SHANTI' : 'Discharged',
      selectUnit: `Discharged From ${child.selectUnit}`,
      dischargeDate: dischargeForm?.dischargeDate,
      isAlloted: false,
      followUpDate: isOmShanti ? null : dischargeForm?.followUpDate,
      reason: dischargeForm?.reason,
      remark: dischargeForm?.remark,
      centreCode: child?.centreCode,
      centreName: child?.centreName,
      admissionData: firebase.firestore.FieldValue.arrayUnion(dischargeArrayObject),

    }
    if (isOmShanti) updateChildData.isOmShanti = true

    // ?  To Update Centre
    const updateCentreCount = this.mFirestore.firestore.doc(`${this.env.centres}/${child.centreCode}`)
    const _childCode = child?.childCode
    const _centreCode = child?.centreCode
    const _firstName = child?.firstName
    const _lastName = child?.lastName
    const _followUpDate = dischargeForm?.followUpDate
    const _remark = dischargeForm?.remark


    const _updateDataReminderData = {
      'childName': _firstName + ' ' + _lastName,
      'centreName': child?.centreName,
      'centreCode': _centreCode,
      'childCode': _childCode,
      'followUpDate': _followUpDate,
      'remark': _remark,

    }
    const _updateReminderNode = `remindersdata.` + _childCode
    // this.mFirestore.firestore.collection('ffddfs').doc('fdfd').update(map)
// ?Reminder Reference
    const setReminderRef = this.mFirestore.firestore.collection(this.env.reminders).doc('followUpChildren')

    //? Loge before operation

    console.log('dischargeArrayObject', dischargeArrayObject)
    console.log('updateCentreCount', updateCentreCount)
    console.log('updateChildData', updateChildData)

    try {
      await this.mFirestore.firestore.runTransaction(async tx => {
        // ? Release the unit only if it is still this child's; a missing or reassigned unit must not block the discharge
        if (child.selectUnit) {
          const unitRef = this.unitRef(child.centreCode, child.selectUnit)
          const unit = await tx.get(unitRef)
          if (unit.exists && unit.data()?.['allotedTo'] === child.childCode) {
            tx.update(unitRef, {allotedTo: 'NA', name: '', status: 'Available'})
          } else {
            console.warn(`Discharge ${child.childCode}: unit ${child.centreCode}/${child.selectUnit} is missing or allotted to someone else; left unchanged`)
          }
        }

        // ? To update child data
        tx.update(updateChildDetailsRef, updateChildData)

        //? update chilren count in centre
        tx.update(updateCentreCount, {
          availableUnits: firebase.firestore.FieldValue.increment(1),
          occupiedUnits: firebase.firestore.FieldValue.increment(-1),
        })

        if (setReminder) {
          tx.update(setReminderRef, {
            [_updateReminderNode]: _updateDataReminderData
          })
        }
      });

      console.log('Unit discharged!');

      const baseLog: any = {...child};
      baseLog.dateOfDischarge = dischargeForm.dischargeDate;
      baseLog.height = dischargeForm.height;
      baseLog.weight = dischargeForm.weight;
      baseLog.selectUnit = child.selectUnit;
      baseLog.centreCode = child.centreCode;
      baseLog.centreName = child.centreName;
      baseLog.remark = dischargeForm.reason;
      baseLog.status = 'Discharged';

      if ((child.admissionData || []).length === 1) {
        await this.mAdmissionLogService.createDischargeAdmissionChildLogObject(baseLog, 'Discharge', this.userData.email);
      } else {
        await this.mAdmissionLogService.createDischargeAdmissionChildLogObject(baseLog, 'Followup Discharge', this.userData.email);
      }

      if (child.lastDocId) {
        await this.mAdmissionLogService.updateCycleOnDischarge(child.lastDocId, dischargeForm.dischargeDate);
      }
    } catch (err) {
      console.error('dischargeBatch failed:', err);
      throw err;
    }
  }

//? To update reminder in folllow up reminder section
  updateReminder(value: any) {
    const _updateReminderNode = `remindersdata.` + value.childCode
    return this.mFirestore.firestore.collection(this.env.reminders).doc('followUpChildren')
      .update({[_updateReminderNode]: value})

  }

  deleteReminder(key: string) {
    const _updateReminderNode = `remindersdata.` + key

    return this.mFirestore.firestore.collection(this.env.reminders).doc('followUpChildren')
      .update({

        [_updateReminderNode]: firebase.firestore.FieldValue.delete()
      })


  }

  reAdmissionProcess(selectedCentre: Centre | undefined | null, selectUnit: string, childCode: string, data: ɵTypedOrUntyped<any, ɵFormGroupValue<any>, any>, child: FullChild) {
    console.log(child)
    console.log('selectUni in readmission', selectUnit)
    console.log('Selected Centre During ReAdmission', selectedCentre)
    // ? Updating Unit Details
    // ? Updating Child Details
    const updateChildDetailsRef = this.mFirestore.firestore.doc(`${this.env.child}/${childCode}`)
    const admissionData = {
      centreCode: selectedCentre?.centreCode,
      currentUnit: selectUnit,
      dateOfAdmission: data?.dateOfAdmission,
      height: data?.height,
      weight: data?.weight,
      tentativeDischargeDate: data?.tentativeDischargeDate,
      type: 'FollowUp In',
      //centerName: data?.centreName
    }

    const updateChildData = {
      status: 'Alloted',
      isAlloted: true,
      selectUnit: selectUnit,
      centreCode: selectedCentre?.centreCode,
      centreName: selectedCentre?.centreName,
      // dischargeDate: data?.dischargeDate,
      // followUpDate: data?.followUpDate,
      // reason: data?.reason,
      // remark: data?.remark,
      admissionData: firebase.firestore.FieldValue.arrayUnion(admissionData)
    }

    // ? Updating Centre
    const updateCentreCount = this.mFirestore.firestore.doc(`${this.env.centres}/${selectedCentre?.centreCode}`)

    const baseLog = {...child}
    baseLog.dateOfAdmission = data.dateOfAdmission,
      baseLog.height = data.height,
      baseLog.weight = data.weight,
      baseLog.selectUnit = selectUnit ?? child.selectUnit,
      baseLog.centreCode = selectedCentre?.centreCode ?? child.centreCode
    baseLog.centreName = selectedCentre?.centreName ?? child.centreName

    // Unit check-and-set, child and centre counters commit together or not at all
    if (!selectedCentre?.centreCode || !selectUnit) {
      return Promise.reject(new UnitError('Please select a centre and a unit before re-admitting the child.'))
    }
    const unitRef = this.unitRef(selectedCentre.centreCode, selectUnit)
    return this.mFirestore.firestore.runTransaction(async tx => {
      await this.assertAvailable(tx, unitRef, selectUnit)
      tx.update(unitRef, {
        allotedTo: childCode,
        name: `${child.firstName} ${child.lastName}`,
        status: 'Alloted'
      })
      tx.update(updateChildDetailsRef, updateChildData)
      tx.update(updateCentreCount, this.occupyCentre(1))
    }).then(() => {
      this.mAdmissionLogService.createFollowupAdmissionChildLogObject(baseLog, 'Followup Admission', this.userData.email)

      this.mAdmissionLogService.createCycleOnReAdmission(child, data.dateOfAdmission, selectedCentre, selectUnit, this.userData.email)
        .catch(err => console.error('Failed to create re-admission cycle doc:', err));
    })
  }

//? Testing fieldvalue delete operation in firesotre
  testDelete() {
    let key = 'map.person2'
    this.mFirestore.collection('MAPTEST').doc('test').update({
      [key]: firebase.firestore.FieldValue.delete()
    })
  }

  async swapUnits(selectedCentre: Centre, fromChild: any, toChild: any, childA: FullChild, childB: FullChild) {
    const centreCode = fromChild.centreCode ?? selectedCentre.centreCode;

    // both units are re-checked, swapped and both children updated in one transaction
    await this.swapCentres(childA, childB, tx => this.swapUnitDocs(tx,
      this.unitRef(centreCode, fromChild.key), fromChild,
      this.unitRef(centreCode, toChild.key), toChild));
  }

  /** Swaps two allotted units inside a transaction, after checking neither changed since the screen loaded them. */
  private async swapUnitDocs(tx: firebase.firestore.Transaction,
                             fromRef: firebase.firestore.DocumentReference, fromChild: any,
                             toRef: firebase.firestore.DocumentReference, toChild: any) {
    await this.assertAllottedTo(tx, fromRef, fromChild.key, fromChild.allotedTo);
    await this.assertAllottedTo(tx, toRef, toChild.key, toChild.allotedTo);
    tx.update(fromRef, {allotedTo: toChild.allotedTo, name: toChild.name});
    tx.update(toRef, {allotedTo: fromChild.allotedTo, name: fromChild.name});
  }

  private async swapCentres(mChildAData: FullChild, mChildBData: FullChild, unitOps: (tx: firebase.firestore.Transaction) => Promise<void>) {
    if (!mChildAData || !mChildBData) throw new UnitError('The child details for one of the units have not loaded yet. Please re-select both units and try again.');

    const childAIndex = mChildAData.admissionData.length - 1;
    const childBIndex = mChildBData.admissionData.length - 1;
    const childAOriginalUnit = mChildAData.selectUnit;
    const childBOriginalUnit = mChildBData.selectUnit;

    const admissionDataA = [
      ...mChildAData.admissionData.slice(0, childAIndex),
      {
        ...mChildAData.admissionData[childAIndex],
        currentUnit: childBOriginalUnit,
        transferFromUnit: childAOriginalUnit,
        transferDate: new Date()
      }
    ];
    const admissionDataB = [
      ...mChildBData.admissionData.slice(0, childBIndex),
      {
        ...mChildBData.admissionData[childBIndex],
        currentUnit: childAOriginalUnit,
        transferFromUnit: childBOriginalUnit,
        transferDate: new Date()
      }
    ];
    const updatesForA = {
      selectUnit: childBOriginalUnit,
      admissionCycle: Math.ceil(admissionDataA.length / 2),
      admissionData: admissionDataA,
    };
    const updatesForB = {
      selectUnit: childAOriginalUnit,
      admissionCycle: Math.ceil(admissionDataB.length / 2),
      admissionData: admissionDataB,
    };

    // unitOps does its reads first: a transaction may not read after it has written
    await this.mFirestore.firestore.runTransaction(async tx => {
      await unitOps(tx);
      tx.set(this.mFirestore.collection(this.env.child).doc(mChildAData.childCode).ref, updatesForA, {merge: true});
      tx.set(this.mFirestore.collection(this.env.child).doc(mChildBData.childCode).ref, updatesForB, {merge: true});
    });

    this.updateCycleDoc(mChildAData, childAOriginalUnit, childBOriginalUnit, mChildAData.centreCode, mChildAData.centreName, mChildAData.centreCode, mChildAData.centreName);
    this.updateCycleDoc(mChildBData, childBOriginalUnit, childAOriginalUnit, mChildBData.centreCode, mChildBData.centreName, mChildBData.centreCode, mChildBData.centreName);

    this.logTransfer({
      type: 'UnitSwap',
      childACode: mChildAData.childCode,
      childAName: `${mChildAData.firstName} ${mChildAData.lastName}`,
      childAFromUnit: childAOriginalUnit,
      childAToUnit: childBOriginalUnit,
      childAFromCentre: mChildAData.centreCode,
      childAToCentre: mChildAData.centreCode,
      childBCode: mChildBData.childCode,
      childBName: `${mChildBData.firstName} ${mChildBData.lastName}`,
      childBFromUnit: childBOriginalUnit,
      childBToUnit: childAOriginalUnit,
      childBFromCentre: mChildBData.centreCode,
      childBToCentre: mChildBData.centreCode,
    });
  }

  async transferToEmptyUnit(fromChild: any, toChild: any, child: FullChild) {
    const lastIdx = child.admissionData.length - 1;
    const originalUnit = child.selectUnit;
    const updatedAdmissionData = [
      ...child.admissionData.slice(0, lastIdx),
      {
        ...child.admissionData[lastIdx],
        currentUnit: toChild.key,
        transferFromUnit: originalUnit,
        transferDate: new Date(),
      },
    ];
    const fromRef = this.unitRef(fromChild.centreCode, fromChild.key);
    const toRef = this.unitRef(fromChild.centreCode, toChild.key);
    await this.mFirestore.firestore.runTransaction(async tx => {
      await this.assertAllottedTo(tx, fromRef, fromChild.key, fromChild.allotedTo);
      await this.assertAvailable(tx, toRef, toChild.key);
      tx.update(fromRef, {allotedTo: 'NA', name: '', status: 'Available'});
      tx.update(toRef, {allotedTo: fromChild.allotedTo, name: fromChild.name, status: 'Alloted'});
      tx.set(
        this.mFirestore.collection(this.env.child).doc(child.childCode).ref,
        {
          selectUnit: toChild.key,
          admissionCycle: Math.ceil(updatedAdmissionData.length / 2),
          admissionData: updatedAdmissionData
        },
        {merge: true}
      );
    });

    this.updateCycleDoc(child, originalUnit, toChild.key, child.centreCode, child.centreName, child.centreCode, child.centreName);

    this.logTransfer({
      type: 'TransferToEmpty',
      childACode: child.childCode,
      childAName: `${child.firstName} ${child.lastName}`,
      childAFromUnit: originalUnit,
      childAToUnit: toChild.key,
      childAFromCentre: child.centreCode,
      childAToCentre: child.centreCode,
    });
  }

  async centreTransfer(fromChild: any, toChild: any, childA: FullChild, childB: FullChild) {
    await this.swapInterCentres(childA, childB, tx => this.swapUnitDocs(tx,
      this.unitRef(fromChild.centreCode, fromChild.key), fromChild,
      this.unitRef(toChild.centreCode, toChild.key), toChild));
  }

  async centreTransferToEmpty(fromChild: any, toChild: any, child: FullChild) {
    // Unit availability check, both units, child record and both centre counts commit in one transaction
    const lastIdx = child.admissionData.length - 1;
    const originalUnit = child.selectUnit;
    const updatedAdmissionData = [
      ...child.admissionData.slice(0, lastIdx),
      {
        ...child.admissionData[lastIdx],
        currentUnit: toChild.key,
        centreCode: toChild.centreCode,
        centreName: toChild.centreName,
        transferFromUnit: originalUnit,
        transferFrom: child.centreCode,
        transferDate: new Date(),
      },
    ];

    const toRef = this.unitRef(toChild.centreCode, toChild.key);
    await this.mFirestore.firestore.runTransaction(async tx => {
      const fromRef = this.unitRef(fromChild.centreCode, fromChild.key);
      await this.assertAllottedTo(tx, fromRef, fromChild.key, fromChild.allotedTo);
      await this.assertAvailable(tx, toRef, toChild.key);

      tx.update(fromRef, {allotedTo: 'NA', name: '', status: 'Available'});
      tx.update(toRef, {allotedTo: fromChild.allotedTo, name: fromChild.name, status: 'Alloted'});

      tx.set(
        this.mFirestore.collection(this.env.child).doc(child.childCode).ref,
        {
          selectUnit: toChild.key,
          centreCode: toChild.centreCode,
          centreName: toChild.centreName,
          admissionCycle: Math.ceil(updatedAdmissionData.length / 2),
          admissionData: updatedAdmissionData,
        },
        {merge: true}
      );

      // From-centre: loses one occupied unit
      tx.update(this.centreRef(fromChild.centreCode), this.occupyCentre(-1));
      // To-centre: gains one occupied unit
      tx.update(this.centreRef(toChild.centreCode), this.occupyCentre(1));
    });

    this.updateCycleDoc(child, originalUnit, toChild.key, child.centreCode, child.centreName, toChild.centreCode, toChild.centreName);

    this.logTransfer({
      type: 'CentreTransferToEmpty',
      childACode: child.childCode,
      childAName: `${child.firstName} ${child.lastName}`,
      childAFromUnit: originalUnit,
      childAToUnit: toChild.key,
      childAFromCentre: child.centreCode,
      childAToCentre: toChild.centreCode,
    });
  }

  private async swapInterCentres(mChildAData: FullChild, mChildBData: FullChild, unitOps: (tx: firebase.firestore.Transaction) => Promise<void>) {
    if (!mChildAData || !mChildBData) throw new UnitError('The child details for one of the units have not loaded yet. Please re-select both units and try again.');

    const childAIndex = mChildAData.admissionData.length - 1;
    const childBIndex = mChildBData.admissionData.length - 1;
    const childAOriginalUnit = mChildAData.selectUnit;
    const childBOriginalUnit = mChildBData.selectUnit;

    const interAdmDataA = [
      ...mChildAData.admissionData.slice(0, childAIndex),
      {
        ...mChildAData.admissionData[childAIndex],
        currentUnit: childBOriginalUnit,
        centreCode: mChildBData.centreCode,
        centreName: mChildBData.centreName,
        transferFromUnit: childAOriginalUnit,
        transferFrom: mChildAData.centreCode,
        transferDate: new Date()
      }
    ];
    const interAdmDataB = [
      ...mChildBData.admissionData.slice(0, childBIndex),
      {
        ...mChildBData.admissionData[childBIndex],
        currentUnit: childAOriginalUnit,
        centreCode: mChildAData.centreCode,
        centreName: mChildAData.centreName,
        transferFromUnit: childBOriginalUnit,
        transferFrom: mChildBData.centreCode,
        transferDate: new Date()
      }
    ];
    const updatesForA = {
      selectUnit: childBOriginalUnit,
      centreCode: mChildBData.centreCode,
      centreName: mChildBData.centreName,
      admissionCycle: Math.ceil(interAdmDataA.length / 2),
      admissionData: interAdmDataA,
    };
    const updatesForB = {
      selectUnit: childAOriginalUnit,
      centreCode: mChildAData.centreCode,
      centreName: mChildAData.centreName,
      admissionCycle: Math.ceil(interAdmDataB.length / 2),
      admissionData: interAdmDataB,
    };

    // unitOps does its reads first: a transaction may not read after it has written
    await this.mFirestore.firestore.runTransaction(async tx => {
      await unitOps(tx);
      tx.set(this.mFirestore.collection(this.env.child).doc(mChildAData.childCode).ref, updatesForA, {merge: true});
      tx.set(this.mFirestore.collection(this.env.child).doc(mChildBData.childCode).ref, updatesForB, {merge: true});
    });

    this.updateCycleDoc(mChildAData, childAOriginalUnit, childBOriginalUnit, mChildAData.centreCode, mChildAData.centreName, mChildBData.centreCode, mChildBData.centreName);
    this.updateCycleDoc(mChildBData, childBOriginalUnit, childAOriginalUnit, mChildBData.centreCode, mChildBData.centreName, mChildAData.centreCode, mChildAData.centreName);

    this.logTransfer({
      type: 'CentreTransfer',
      childACode: mChildAData.childCode,
      childAName: `${mChildAData.firstName} ${mChildAData.lastName}`,
      childAFromUnit: childAOriginalUnit,
      childAToUnit: childBOriginalUnit,
      childAFromCentre: mChildAData.centreCode,
      childAToCentre: mChildBData.centreCode,
      childBCode: mChildBData.childCode,
      childBName: `${mChildBData.firstName} ${mChildBData.lastName}`,
      childBFromUnit: childBOriginalUnit,
      childBToUnit: childAOriginalUnit,
      childBFromCentre: mChildBData.centreCode,
      childBToCentre: mChildAData.centreCode,
    });
  }

  private logTransfer(entry: {
    type: string;
    childACode: string;
    childAName: string;
    childAFromUnit: string;
    childAToUnit: string;
    childAFromCentre: string;
    childAToCentre: string;
    childBCode?: string;
    childBName?: string;
    childBFromUnit?: string;
    childBToUnit?: string;
    childBFromCentre?: string;
    childBToCentre?: string;
  }) {
    const userData = JSON.parse(localStorage.getItem('userData') || '{}');
    this.mFirestore.collection(this.env.unitTransferLogs).add({
      ...entry,
      date: new Date(),
      transferredBy: userData?.email || userData?.userName || 'Unknown',
    }).catch(err => console.error('Transfer log write failed:', err));
  }

  private updateCycleDoc(
    child: FullChild,
    fromUnit: string, toUnit: string,
    fromCentreCode: string, fromCentreName: string,
    toCentreCode: string, toCentreName: string
  ): void {
    if (!child.lastDocId) return;
    const userData = JSON.parse(localStorage.getItem('userData') || '{}');
    const by = userData?.email || userData?.userName || 'Unknown';
    const locationChanged = fromCentreCode !== toCentreCode;
    this.mAdmissionLogService.updateCycleOnTransfer(
      child.lastDocId,
      {fromCentreCode, fromCentreName, fromUnit, toCentreCode, toCentreName, toUnit, date: new Date(), by},
      locationChanged ? {
        centreCode: toCentreCode,
        centreName: toCentreName,
        currentUnit: toUnit
      } : {centreCode: toCentreCode, centreName: toCentreName, currentUnit: toUnit}
    ).catch(err => console.error('Failed to update ALAdmissionCycles:', err));
  }




}
